/**
 * First-party behavioral event tracking.
 *
 * Fire-and-forget POST to the API's /api/events endpoint so storefront behavior
 * (product views, add-to-cart) is recorded in the first-party events table and
 * becomes agent-readable analytics. Never throws and swallows its own failures
 * (including network errors) so tracking can never break the app.
 *
 * The server only accepts client event types (product_view, collection_view,
 * story_view, add_to_cart); money-bearing events (checkout_start, purchase)
 * are emitted server-side.
 *
 * Every event is auto-tagged with a stable per-browser visitor id (so funnels can
 * be measured per session) and, when an experiment is running, the visitor's
 * assigned variant (so per-variant exposure can be measured). Call sites don't
 * need to pass these.
 *
 * @format
 */

import Axios from "axios";
import type { ClientEventType, ExperimentSurface } from "@outstanding/shared";
import {
  ATTRIBUTION_VALUE_MAX_LENGTH,
  EXPERIMENT_KEY_MAX_LENGTH,
} from "@outstanding/shared";
import {
  assignmentsPolicy,
  attributionPolicy,
  readClientState,
  visitorIdPolicy,
  writeClientState,
} from "@/utils/clientState";

// The client-emittable event types come from @outstanding/shared (bead 49n.3a) —
// the SAME array the API sink's allow-set is built from, so an addition here
// can't be silently 204-dropped by the server. Members: product_view,
// collection_view, story_view, add_to_cart, and `exposure` (bead lwb — the bare
// experiment-exposure signal a surface with no natural content event fires,
// carrying only the experiment tag).
export type { ClientEventType };

// Storage lifecycle (versioned envelope, legacy migration, expiry) for
// visitor_id / experiment_assignments / first_touch_attribution is owned by
// their policies in utils/clientState (outstanding-qbo.10); this module owns
// the SEMANTICS (first-touch wins, overwrite-on-successful-fetch, …).

/**
 * First-touch acquisition attribution captured on the visitor's first landing —
 * which channel/campaign + referrer + entry page brought them. Persisted so it
 * carries through to the eventual order (revenue attribution). All optional.
 */
export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_path?: string;
}

const ATTRIBUTION_PARAMS: Array<keyof Attribution> = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

// UTM + experiment-tag caps come from @outstanding/shared (bead 49n.3b) — the
// SAME numbers the server's cart schema and events sink enforce, so a value that
// passes the clamp here can't be re-truncated (or rejected) server-side.

// UTM values are bounded so we never persist or forward an arbitrarily long
// attacker-supplied query string. The server validates again.
function clampAttr(v: string): string {
  return v.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH);
}

// Experiment key/variant are short slugs. Bound them before forwarding so a
// tampered assignment can't send an oversized tag; the server caps again.
function clampKey(v: string): string {
  return v.slice(0, EXPERIMENT_KEY_MAX_LENGTH);
}

/**
 * Known search engines, matched on the referrer host. Regional domains count
 * (google.co.uk, search.yahoo.co.jp), so match a dotted segment rather than an
 * exact host. Anything not listed is treated as a plain referral.
 */
function searchEngineSource(host: string): string | null {
  if (/(^|\.)google\./.test(host)) return "google";
  if (/(^|\.)bing\./.test(host)) return "bing";
  if (/(^|\.)duckduckgo\./.test(host)) return "duckduckgo";
  if (/(^|\.)yahoo\./.test(host)) return "yahoo";
  if (/(^|\.)ecosia\./.test(host)) return "ecosia";
  if (/(^|\.)startpage\./.test(host)) return "startpage";
  if (/(^|\.)brave\./.test(host)) return "brave";
  if (/(^|\.)yandex\./.test(host)) return "yandex";
  if (/(^|\.)baidu\./.test(host)) return "baidu";
  return null;
}

/**
 * Derive a channel from an EXTERNAL referrer: a search engine is `organic`,
 * anything else is a `referral` sourced by its bare host. Returns null for a
 * same-site navigation (not an acquisition) or a referrer we can't trust as a
 * web origin — non-http(s) schemes are excluded because `android-app://…` URLs
 * DO parse, and their package hosts (com.google.android.gm) would otherwise be
 * mis-read as search engines.
 */
function channelFromReferrer(
  referrer: string,
  currentHost: string,
): { utm_source: string; utm_medium: string } | null {
  let host: string;
  try {
    const url = new URL(referrer);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    host = url.hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!host) return null;

  const bare = host.replace(/^www\./, "");
  const current = currentHost.toLowerCase().replace(/^www\./, "");
  if (current && bare === current) return null; // internal nav, not an acquisition

  const engine = searchEngineSource(bare);
  return engine
    ? { utm_source: engine, utm_medium: "organic" }
    : { utm_source: bare, utm_medium: "referral" };
}

/**
 * Parse first-touch attribution from a URL's query + a referrer. Pure/exported so
 * it can be unit-tested without a DOM. Only includes keys that are present.
 *
 * Organic search is the ONE channel that cannot carry UTMs — you cannot append
 * `?utm_source=` to a Google result. Since channel_analytics GROUPs BY
 * utm_source/medium/campaign, an untagged organic visitor would otherwise land in
 * the same NULL bucket as direct traffic and the scoreboard could never show
 * organic working. So a WHOLLY UNTAGGED landing with an external referrer gets a
 * synthesized channel (google/organic, pinterest.com/referral, …). Explicit tags
 * always win — we never overwrite a real campaign.
 */
export function parseAttribution(
  search: string,
  referrer: string,
  path: string,
  currentHost = "",
): Attribution {
  const attribution: Attribution = {};
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search || "");
  } catch {
    params = new URLSearchParams();
  }
  for (const key of ATTRIBUTION_PARAMS) {
    const value = params.get(key);
    if (value) attribution[key] = clampAttr(value);
  }
  if (referrer) attribution.referrer = clampAttr(referrer);
  if (path) attribution.landing_path = clampAttr(path);

  const tagged = ATTRIBUTION_PARAMS.some((key) => attribution[key]);
  if (!tagged && referrer) {
    const derived = channelFromReferrer(referrer, currentHost);
    if (derived) {
      attribution.utm_source = clampAttr(derived.utm_source);
      attribution.utm_medium = derived.utm_medium;
    }
  }
  return attribution;
}

/**
 * Capture first-touch attribution on first load and persist it (localStorage).
 * FIRST-TOUCH wins: while stored it is never overwritten, so a later
 * add-to-cart from an internal navigation can't clobber the channel that
 * originally acquired the visitor. A landing with no UTMs and no referrer
 * (direct/unknown) stores nothing, leaving the door open for a later UTM'd
 * touch in the same browser.
 *
 * Expiry (outstanding-qbo.10, OWNER-DECIDED 2026-07-05): the stored touch
 * carries a 90-day TTL counted in QUIET days — this capture re-stamps the
 * envelope's `at` on every app load, so an actively returning visitor keeps
 * their first touch while a re-touch after 90 silent days becomes the NEW
 * first touch (previously attribution was first-touch-FOREVER).
 *
 * Best-effort — never throws (private mode / SSR).
 */
export function captureFirstTouchAttribution(): Attribution {
  try {
    const existing = readClientState(attributionPolicy);
    if (existing) {
      // Refresh the quiet-day clock: same data, new `at`.
      writeClientState(attributionPolicy, existing);
      return existing;
    }

    const search = typeof window !== "undefined" ? window.location.search : "";
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const referrer = typeof document !== "undefined" ? document.referrer : "";
    // `host` lets an internal referrer be recognized as a same-site navigation
    // rather than a self-referral "acquisition".
    const attribution = parseAttribution(search, referrer, path, host);

    // Only persist a real first touch — i.e. a UTM-tagged or referred landing.
    // A bare direct hit stores nothing so a later attributable touch still wins.
    if (attribution.utm_source || attribution.utm_medium || attribution.referrer) {
      // Only report a touch that actually landed durably (quota / private
      // mode ⇒ {}), matching the pre-envelope behavior.
      return writeClientState(attributionPolicy, attribution) ? attribution : {};
    }
    return {};
  } catch {
    return {};
  }
}

/** The persisted (unexpired) first-touch attribution, or {} when none / unavailable. */
export function getAttribution(): Attribution {
  return readClientState(attributionPolicy) ?? {};
}

/** One experiment assignment as returned by GET /api/experiments/assignments. */
export interface ExperimentAssignment {
  key: string;
  surface: string;
  variant: string;
  payload?: unknown;
  // Server-issued HMAC token binding this (visitorId, key, variant); echoed on
  // exposure events so the sink can verify the attribution (bead gud). Absent
  // when the API's exposure secret isn't provisioned (sink accepts the tag).
  token?: string;
  // Winner-locked serving (bead qbo.8): a CONCLUDED experiment whose winning
  // variant is served to 100% of visitors. The API sends these PAYLOAD-ONLY —
  // unsigned (no token) precisely so the events sink drops any exposure — so the
  // SPA must NOT accrue exposure or order attribution for them either (bead
  // k5hd): they render their payload but never tag an event or the exposure
  // ledger, keeping concluded results frozen.
  locked?: boolean;
}

// In-memory mirror of the visitor's assignments for the current load. Persisted to
// localStorage too, so a later page (e.g. Cart after a reload) can still attribute.
let currentAssignments: ExperimentAssignment[] = [];

/**
 * Stable per-browser visitor id (localStorage). Generated lazily on first use.
 * Falls back to a transient id when storage is unavailable (private mode/SSR) so
 * tracking never throws.
 */
export function getVisitorId(): string {
  try {
    const existing = readClientState(visitorIdPolicy);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // A visitor id is only useful when it's STABLE: if it can't be persisted
    // (private mode / quota) fall back to "anon" rather than minting a fresh
    // id per call (matches the pre-envelope behavior).
    return writeClientState(visitorIdPolicy, id) ? id : "anon";
  } catch {
    return "anon";
  }
}

// Memoized per-load assignments fetch (bead zpe.7). Fetched ONCE at app level
// (App.vue setup) so every entry point has assignments — before this, only the
// Homepage fetched, and a visitor landing directly on a photo/collection/cart
// page never received payloads or exposure tokens at all. Best-effort like all
// tracking: failure resolves to [] and the storefront renders its static
// defaults.
let assignmentsFetch: Promise<ExperimentAssignment[]> | null = null;
export function ensureExperimentAssignments(): Promise<ExperimentAssignment[]> {
  if (!assignmentsFetch) {
    assignmentsFetch = Axios.get(
      `/api/experiments/assignments?visitorId=${encodeURIComponent(getVisitorId())}`,
    )
      .then((res) => {
        const assignments: ExperimentAssignment[] =
          res?.data?.data?.assignments ?? [];
        setExperimentAssignments(assignments);
        return assignments;
      })
      .catch(() => {
        // Do NOT memoize failure: a transient blip at app start must not
        // disable all experiments (and untag all exposure) for the whole SPA
        // session — the next caller retries (2026-07-04 solidity review).
        assignmentsFetch = null;
        return [] as ExperimentAssignment[];
      });
  }
  return assignmentsFetch;
}

/**
 * The visitor's assignment for the experiment rendered on `surface`, or null.
 * Payload-consuming components resolve by surface (the Homepage hero pattern)
 * so the experiment KEY stays server-side data, not code.
 */
export function getAssignmentBySurface(
  // ExperimentSurface, NOT `string`. A surface that does not exist — a typo, or
  // a name left stale after PATCH /admin/experiments/* relabels a RUNNING
  // experiment onto a different surface — returns null silently, and the
  // component renders control forever: no error, no failing test, and the
  // experiment quietly measures nothing. The shared union is the only thing
  // that can catch it, and this module already imports from
  // @outstanding/shared.
  surface: ExperimentSurface,
): ExperimentAssignment | null {
  return currentAssignments.find((a) => a.surface === surface) ?? null;
}

/** Record the visitor's experiment assignments for this load (memory + storage). */
export function setExperimentAssignments(assignments: ExperimentAssignment[]): void {
  currentAssignments = Array.isArray(assignments) ? assignments : [];
  // Persist EXACTLY the map derived from THIS call's assignments — including
  // {} for an empty array. Serializing getExperimentAssignmentMap() here read
  // the OLD persisted map back whenever the array was empty (its localStorage
  // fallback), so a kill-switch / concluded experiment never cleared storage:
  // key-gated consumers (Story, CollectionFaq) and order attribution kept the
  // dead assignment forever. A FAILED fetch still keeps the stale persisted
  // map — ensureExperimentAssignments' catch never calls this setter.
  // (writeClientState never throws; persistence is best-effort.)
  const map = Object.fromEntries(
    currentAssignments.map((a) => [a.key, a.variant]),
  );
  writeClientState(assignmentsPolicy, map);
}

/**
 * The visitor's assignments as a { experimentKey: variant } map — the shape the
 * order-creation endpoint stores for conversion attribution. Falls back to the
 * persisted map when nothing has been set this load.
 */
export function getExperimentAssignmentMap(): Record<string, string> {
  if (currentAssignments.length > 0) {
    return Object.fromEntries(currentAssignments.map((a) => [a.key, a.variant]));
  }
  // Storage fallback: readClientState migrates legacy raw { key: variant }
  // maps on read (pre-envelope visitors keep their bucket) and clears
  // corrupt/unknown values.
  return readClientState(assignmentsPolicy) ?? {};
}

// --- Per-session exposure ledger (outstanding-qbo.8, H3) --------------------
// Conversions used to attribute every buyer's FULL assignment map while
// exposure only counted surface-visitors, so cross-experiment comparisons
// were apples/oranges and low-traffic surfaces were permanently
// not-significant. trackEvent records here the keys whose experiment tag an
// event ACTUALLY carried this session (the `exposed` branch below fired);
// checkout sends only that subset (see useCheckout). Backed by sessionStorage
// so a reload mid-funnel doesn't lose it, with an in-memory mirror so a
// storage-less context (private mode) still works within the page load.
/** sessionStorage key holding the exposure-tagged experiment keys (JSON string[]). */
export const EXPOSED_EXPERIMENTS_KEY = "exposed_experiments";
// Bound the persisted set so hostile/buggy data can't grow it unboundedly.
const EXPOSED_KEYS_MAX = 50;
const exposedThisLoad = new Set<string>();

function persistedExposedKeys(): string[] {
  try {
    if (typeof sessionStorage === "undefined") return [];
    const raw = sessionStorage.getItem(EXPOSED_EXPERIMENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((key): key is string => typeof key === "string");
  } catch {
    return [];
  }
}

/**
 * The experiment keys for which an event actually carried the experiment tag
 * this SESSION (per-tab; survives a reload mid-funnel). Order creation sends
 * only this subset of the assignment map, so conversion attribution matches
 * the exposure population per surface (outstanding-qbo.8).
 */
export function getExposedExperimentKeys(): string[] {
  return [...new Set([...persistedExposedKeys(), ...exposedThisLoad])];
}

function recordExposedExperimentKey(key: string): void {
  exposedThisLoad.add(key);
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(
      EXPOSED_EXPERIMENTS_KEY,
      JSON.stringify(getExposedExperimentKeys().slice(0, EXPOSED_KEYS_MAX)),
    );
  } catch {
    // Per-tab persistence is best-effort; the in-memory set still covers
    // this page load.
  }
}

/** Test hook: clear the per-session exposure ledger (memory + sessionStorage). */
export function resetExposedExperimentKeysForTests(): void {
  exposedThisLoad.clear();
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(EXPOSED_EXPERIMENTS_KEY);
    }
  } catch {
    // ignore
  }
}

export interface TrackEventInput {
  type: ClientEventType;
  photoId?: number | null;
  collectionId?: number | null;
  optionIds?: number[] | null;
  sessionId?: string | null;
  value?: number | null;
  metadata?: Record<string, unknown> | null;
  /**
   * Per-surface exposure attribution (bead lwb): the experiment whose SURFACE
   * this event witnesses (e.g. collection_view → "collection_faq"). The event
   * is tagged with the visitor's assignment for THAT experiment only — and only
   * when the visitor actually holds one — so concurrent experiments each
   * accumulate exposure from their own surface instead of whichever assignment
   * happened to be first. Omit for events with no experiment surface; they
   * carry no experiment tag at all.
   */
  experimentKey?: string | null;
}

/**
 * Record a first-party event. Fire-and-forget: never rejects, never throws.
 * Auto-attaches the visitor id (as sessionId) and the visitor's first-touch
 * channel (utm_source/medium/campaign). Experiment exposure is PER-SURFACE
 * (bead lwb): the event is tagged only when the caller names the surface's
 * experiment via `experimentKey` AND the visitor holds an assignment for it —
 * never with an arbitrary "first" assignment, which starved every concurrent
 * experiment but one of exposure.
 */
export function trackEvent(input: TrackEventInput): void {
  try {
    const { experimentKey: surfaceKey, ...event } = input;
    // A winner-LOCKED assignment (bead k5hd) is payload-only: it renders but
    // must accrue no exposure or attribution (its experiment is concluded), so
    // it never counts as `exposed` — no event tag, no exposure-ledger entry,
    // no order attribution. Mirrors the server, which sends it unsigned so the
    // sink would drop the tag anyway; this closes the SPA-side paths the sink
    // doesn't gate (the exposure ledger + checkout's exposed-only map).
    const exposed = surfaceKey
      ? currentAssignments.find((a) => a.key === surfaceKey && !a.locked)
      : undefined;
    // This event will carry the experiment tag — record the key in the
    // per-session exposure ledger (qbo.8) BEFORE the fire-and-forget POST, so
    // a failed send still marks the visitor as exposure-tagged this session.
    if (exposed) recordExposedExperimentKey(exposed.key);
    const attribution = getAttribution();
    const payload = {
      ...event,
      sessionId: event.sessionId ?? getVisitorId(),
      ...(exposed
        ? {
            experimentKey: clampKey(exposed.key),
            variant: clampKey(exposed.variant),
            // Echo the server-signed exposure token so the sink records the
            // attribution (bead gud). Older API responses omit it.
            ...(exposed.token ? { exposureToken: exposed.token } : {}),
          }
        : {}),
      ...(attribution.utm_source ? { utmSource: attribution.utm_source } : {}),
      ...(attribution.utm_medium ? { utmMedium: attribution.utm_medium } : {}),
      ...(attribution.utm_campaign ? { utmCampaign: attribution.utm_campaign } : {}),
    };
    // Uses the app's axios instance so it inherits the configured baseURL
    // (direct API origin in prod, Vite proxy in dev).
    void Axios.post("/api/events", payload).catch(() => {
      // Swallow: analytics failures must never surface.
    });
  } catch {
    // Swallow any synchronous failure.
  }
}

export default trackEvent;
